import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import cytoscape, { Core, EventObject } from 'cytoscape';
import dagre from 'cytoscape-dagre';
import { X, Crosshair } from 'lucide-react';

cytoscape.use(dagre);

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#eab308',
  low: '#3b82f6',
  info: '#6b7280',
};

interface ATTCKPhase {
  id: string;
  label: string;
  scanTypes: string[];
}

const ATTCK_PHASES: ATTCKPhase[] = [
  { id: 'recon', label: 'Reconnaissance', scanTypes: ['subdomain', 'subfinder', 'surface_discovery', 'tech'] },
  { id: 'initial-access', label: 'Initial Access', scanTypes: ['sqli_test', 'xss_test', 'brute_login', 'dir_traversal'] },
  { id: 'execution', label: 'Execution', scanTypes: [] },
  { id: 'persistence', label: 'Persistence', scanTypes: [] },
  { id: 'priv-esc', label: 'Privilege Escalation', scanTypes: [] },
  { id: 'defense-evasion', label: 'Defense Evasion', scanTypes: ['headers', 'clickjack_poc', 'csrf_active'] },
  { id: 'cred-access', label: 'Credential Access', scanTypes: ['darkweb_intel', 'session_analysis', 'user_enum'] },
  { id: 'discovery', label: 'Discovery', scanTypes: ['nmap', 'ports', 'admin_panels', 'js_analysis', 'info_disclosure'] },
  { id: 'lateral-movement', label: 'Lateral Movement', scanTypes: [] },
  { id: 'collection', label: 'Collection', scanTypes: ['tenant_enum'] },
  { id: 'exfiltration', label: 'Exfiltration', scanTypes: [] },
  { id: 'impact', label: 'Impact', scanTypes: ['rate_limit_test'] },
];

function mapFindingToPhase(scanType: string): string | null {
  for (const phase of ATTCK_PHASES) {
    if (phase.scanTypes.includes(scanType)) {
      return phase.id;
    }
  }
  return null;
}

interface KillChainGraphProps {
  findings: Record<string, unknown>[];
  target: string;
}

export default function KillChainGraph({ findings, target }: KillChainGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null);

  const { elements, gapPhases, coveredCount } = useMemo(() => {
    const nodes: cytoscape.ElementDefinition[] = [];
    const edges: cytoscape.ElementDefinition[] = [];
    const gaps: string[] = [];
    let covered = 0;

    const phaseFindings = new Map<string, Record<string, unknown>[]>();
    for (const phase of ATTCK_PHASES) {
      phaseFindings.set(phase.id, []);
    }

    for (const finding of findings) {
      const scanType = finding.scan_type as string;
      if (!scanType) continue;
      const phaseId = mapFindingToPhase(scanType);
      if (phaseId) {
        phaseFindings.get(phaseId)?.push(finding);
      }
    }

    for (const phase of ATTCK_PHASES) {
      const phaseItems = phaseFindings.get(phase.id) || [];
      const hasFindings = phaseItems.length > 0;
      if (hasFindings) covered++;

      nodes.push({
        data: {
          id: `phase-${phase.id}`,
          label: phase.label,
          type: 'phase',
          count: phaseItems.length,
          isGap: !hasFindings,
        },
      });

      if (!hasFindings) {
        gaps.push(phase.label);
      }

      const capped = phaseItems.slice(0, 6);
      for (let i = 0; i < capped.length; i++) {
        const f = capped[i];
        const nodeId = `finding-${phase.id}-${i}`;
        const title = (f.title as string) || (f.scan_type as string) || 'Unknown';
        const severity = (f.severity as string) || 'info';

        nodes.push({
          data: {
            id: nodeId,
            label: title.length > 35 ? title.substring(0, 32) + '...' : title,
            type: 'finding',
            severity,
            scanType: f.scan_type,
            parent: `phase-${phase.id}`,
            finding: f,
          },
        });

        edges.push({
          data: {
            id: `edge-${phase.id}-${i}`,
            source: `phase-${phase.id}`,
            target: nodeId,
          },
        });
      }
    }

    for (let i = 0; i < ATTCK_PHASES.length - 1; i++) {
      edges.push({
        data: {
          id: `flow-${i}`,
          source: `phase-${ATTCK_PHASES[i].id}`,
          target: `phase-${ATTCK_PHASES[i + 1].id}`,
          type: 'flow',
        },
      });
    }

    return {
      elements: [...nodes, ...edges],
      gapPhases: gaps,
      coveredCount: covered,
    };
  }, [findings]);

  const handleNodeTap = useCallback((evt: EventObject) => {
    const node = evt.target;
    const data = node.data();
    if (data.type === 'finding' && data.finding) {
      setSelected(data.finding as Record<string, unknown>);
    }
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: [
        {
          selector: 'node[type="phase"]',
          style: {
            'label': 'data(label)',
            'text-valign': 'top',
            'text-halign': 'center',
            'font-size': '11px',
            'font-weight': 'bold',
            'color': '#94a3b8',
            'background-color': 'mapData(isGap, 0, 1, rgba(51,65,85,0.4), rgba(71,85,105,0.15))',
            'border-width': 'mapData(isGap, 0, 1, 2, 1)',
            'border-color': 'mapData(isGap, 0, 1, #475569, #64748b)',
            'border-style': 'mapData(isGap, 0, 1, solid, dashed)' as unknown as string,
            'shape': 'round-rectangle',
            'width': 140,
            'height': 50,
            'text-margin-y': -4,
          } as unknown as cytoscape.Css.Node,
        },
        {
          selector: 'node[type="phase"][?isGap]',
          style: {
            'border-style': 'dashed',
            'background-color': 'rgba(71,85,105,0.15)',
            'border-color': '#64748b',
            'border-width': 1,
          } as unknown as cytoscape.Css.Node,
        },
        {
          selector: 'node[type="phase"][!isGap]',
          style: {
            'border-style': 'solid',
            'background-color': 'rgba(51,65,85,0.4)',
            'border-color': '#475569',
            'border-width': 2,
          } as unknown as cytoscape.Css.Node,
        },
        {
          selector: 'node[type="finding"]',
          style: {
            'label': 'data(label)',
            'text-valign': 'center',
            'text-halign': 'center',
            'font-size': '8px',
            'color': '#ffffff',
            'text-wrap': 'wrap',
            'text-max-width': '110px',
            'width': 120,
            'height': 36,
            'shape': 'round-rectangle',
            'border-width': 1,
          } as unknown as cytoscape.Css.Node,
        },
        {
          selector: 'node[severity="critical"]',
          style: { 'background-color': '#dc2626', 'border-color': '#b91c1c' },
        },
        {
          selector: 'node[severity="high"]',
          style: { 'background-color': '#ea580c', 'border-color': '#c2410c' },
        },
        {
          selector: 'node[severity="medium"]',
          style: { 'background-color': '#eab308', 'border-color': '#ca8a04', 'color': '#1f2937' },
        },
        {
          selector: 'node[severity="low"]',
          style: { 'background-color': '#3b82f6', 'border-color': '#2563eb' },
        },
        {
          selector: 'node[severity="info"]',
          style: { 'background-color': '#6b7280', 'border-color': '#4b5563' },
        },
        {
          selector: 'edge[type="flow"]',
          style: {
            'width': 2,
            'line-color': '#334155',
            'target-arrow-color': '#334155',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'arrow-scale': 0.8,
          } as unknown as cytoscape.Css.Edge,
        },
        {
          selector: 'edge[!type]',
          style: {
            'width': 1,
            'line-color': '#475569',
            'curve-style': 'bezier',
            'opacity': 0.5,
          } as unknown as cytoscape.Css.Edge,
        },
      ],
      layout: {
        name: 'dagre',
        rankDir: 'LR',
        nodeSep: 30,
        rankSep: 60,
        edgeSep: 10,
        padding: 20,
      } as unknown as cytoscape.LayoutOptions,
      userZoomingEnabled: true,
      userPanningEnabled: true,
      boxSelectionEnabled: false,
    });

    cy.on('tap', 'node[type="finding"]', handleNodeTap);
    cyRef.current = cy;

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [elements, handleNodeTap]);

  const actionableCount = findings.filter(f => f.severity !== 'info').length;
  if (actionableCount === 0 && findings.length === 0) return null;

  return (
    <div className="card !p-0 overflow-hidden print:hidden relative" style={{ height: '420px' }}>
      <div className="px-4 py-3 bg-gradient-to-r from-gray-900 to-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crosshair className="h-5 w-5 text-orange-400" />
          <h3 className="text-sm font-bold text-white">MITRE ATT&CK Kill Chain</h3>
          <span className="text-[10px] bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded-full font-medium">
            {coveredCount}/{ATTCK_PHASES.length} fases cubiertas
          </span>
        </div>
        <div className="flex items-center gap-3 text-[9px]">
          {Object.entries(SEVERITY_COLORS).map(([sev, color]) => {
            const count = findings.filter(f => f.severity === sev).length;
            if (!count) return null;
            return (
              <span key={sev} className="flex items-center gap-1">
                <span style={{ width: 8, height: 8, borderRadius: 4, background: color, display: 'inline-block' }} />
                <span style={{ color: '#9ca3af' }}>{count} {sev}</span>
              </span>
            );
          })}
        </div>
      </div>

      {gapPhases.length > 0 && (
        <div className="absolute top-12 left-4 z-10 bg-slate-800/90 backdrop-blur rounded-lg px-3 py-2 max-w-xs">
          <p className="text-[10px] text-amber-400 font-semibold mb-1">Brechas en defensa detectadas:</p>
          <p className="text-[9px] text-slate-400 leading-relaxed">
            {gapPhases.join(', ')}
          </p>
        </div>
      )}

      <div ref={containerRef} style={{ width: '100%', height: 'calc(100% - 44px)', background: '#0f172a' }} />

      {selected && (
        <div className="absolute top-14 right-4 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[340px] overflow-y-auto">
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span
                className="px-2 py-0.5 rounded text-[10px] font-bold text-white"
                style={{ background: SEVERITY_COLORS[(selected.severity as string)] || '#6b7280' }}
              >
                {((selected.severity as string) || '').toUpperCase()}
              </span>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <h4 className="font-bold text-sm text-gray-900">{selected.title as string}</h4>
            <p className="text-[10px] text-gray-500 mt-0.5">{selected.scan_type as string}</p>
            {typeof selected.description === 'string' && selected.description && (
              <p className="text-xs text-gray-600 mt-2">{selected.description.substring(0, 200)}</p>
            )}
            {typeof selected.evidence === 'string' && selected.evidence && (
              <div className="mt-2 p-2 bg-gray-50 rounded text-[10px] font-mono text-gray-600 break-all">
                {selected.evidence.substring(0, 150)}
              </div>
            )}
            {typeof selected.remediation === 'string' && selected.remediation && (
              <div className="mt-2 p-2 bg-emerald-50 rounded text-[10px] text-emerald-700">
                <strong>Fix:</strong> {selected.remediation.substring(0, 150)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
